import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Icon, LatLngBounds, LatLng, DivIcon } from 'leaflet';
import { Report } from '../hooks/useReports';
import TreasureMapPopup from './TreasureMapPopup';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

// Fix for default markers in React Leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapViewProps {
  reports: Report[];
  selectedReport: Report | null;
  onReportSelect: (report: Report | null) => void;
  center?: [number, number] | null;
  zoom?: number;
  onMapMove?: (center: [number, number], zoom: number) => void;
  isLoading?: boolean;
  hoveredReport?: Report | null;
  onMapBoundsChange?: (bounds: { north: number; south: number; east: number; west: number }, isUserInitiated?: boolean) => void;
}

// Custom marker icons for different statuses
const createCustomIcon = (status: string, isSelected: boolean = false, isHovered: boolean = false) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#648A51'; // verge green
      case 'going_soon':
        return '#FFD96C'; // treasure gold
      case 'gone':
        return '#C1533D'; // rust red
      default:
        return '#5D5149'; // urban asphalt
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return '🏆';
      case 'going_soon':
        return '⏰';
      case 'gone':
        return '❌';
      default:
        return '📦';
    }
  };

  const color = getStatusColor(status);
  const icon = getStatusIcon(status);
  const baseSize = 32;
  const size = isSelected || isHovered ? 40 : baseSize;
  const borderWidth = isSelected || isHovered ? 3 : 2;
  const shadowIntensity = isSelected || isHovered ? 0.4 : 0.2;
  const shadowBlur = isSelected || isHovered ? 12 : 8;

  return new DivIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: ${borderWidth}px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${size * 0.4}px;
        box-shadow: 0 2px ${shadowBlur}px rgba(0, 0, 0, ${shadowIntensity});
        cursor: pointer;
        transition: all 0.2s ease;
        transform: ${isSelected || isHovered ? 'scale(1.1)' : 'scale(1)'};
        z-index: ${isSelected || isHovered ? 998 : 100};
        ${isHovered && !isSelected ? 'animation: markerPulse 1s infinite;' : ''}
      " class="treasure-marker ${isSelected ? 'marker-selected' : ''} ${isHovered ? 'marker-hovered' : ''}">
        ${icon}
      </div>
      <style>
        @keyframes markerPulse {
          0%, 100% { transform: scale(1.1); }
          50% { transform: scale(1.2); }
        }
      </style>
    `,
    className: 'custom-div-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Cluster icon for multiple reports in the same area
const createClusterIcon = (count: number) => {
  const size = Math.min(50, Math.max(30, 20 + count * 2));

  return new DivIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: linear-gradient(135deg, #C58B3A, #FFD96C);
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${size * 0.3}px;
        font-weight: bold;
        color: #5D5149;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        cursor: pointer;
      " class="cluster-marker">
        ${count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Simple clustering logic - group reports within a certain distance
const clusterReports = (reports: Report[], zoomLevel: number): Array<{
  reports: Report[];
  center: [number, number];
  isCluster: boolean;
}> => {
  if (zoomLevel >= 15) {
    // At high zoom levels, don't cluster
    return reports.map(report => ({
      reports: [report],
      center: [report.location.latitude, report.location.longitude] as [number, number],
      isCluster: false,
    }));
  }

  const clusters: Array<{
    reports: Report[];
    center: [number, number];
    isCluster: boolean;
  }> = [];

  const processed = new Set<string>();
  const clusterDistance = zoomLevel >= 12 ? 0.001 : 0.005; // Adjust clustering distance based on zoom

  reports.forEach(report => {
    if (processed.has(report.id)) return;

    const cluster = {
      reports: [report],
      center: [report.location.latitude, report.location.longitude] as [number, number],
      isCluster: false,
    };

    // Find nearby reports to cluster
    reports.forEach(otherReport => {
      if (processed.has(otherReport.id) || report.id === otherReport.id) return;

      const distance = Math.sqrt(
        Math.pow(report.location.latitude - otherReport.location.latitude, 2) +
        Math.pow(report.location.longitude - otherReport.location.longitude, 2)
      );

      if (distance < clusterDistance) {
        cluster.reports.push(otherReport);
        processed.add(otherReport.id);
      }
    });

    // Calculate cluster center
    if (cluster.reports.length > 1) {
      const avgLat = cluster.reports.reduce((sum, r) => sum + r.location.latitude, 0) / cluster.reports.length;
      const avgLng = cluster.reports.reduce((sum, r) => sum + r.location.longitude, 0) / cluster.reports.length;
      cluster.center = [avgLat, avgLng];
      cluster.isCluster = true;
    }

    clusters.push(cluster);
    processed.add(report.id);
  });

  return clusters;
};

// Component to handle map events and updates
const MapController: React.FC<{
  center?: [number, number] | null;
  zoom?: number;
  onMapMove?: (center: [number, number], zoom: number) => void;
  onMapBoundsChange?: (bounds: { north: number; south: number; east: number; west: number }, isUserInitiated?: boolean) => void;
  reports: Report[];
}> = ({ center, zoom, onMapMove, onMapBoundsChange, reports }) => {
  const map = useMap();
  const isUpdatingRef = useRef(false);
  const boundsChangeTimeoutRef = useRef<NodeJS.Timeout>();
  const hasInitializedRef = useRef(false);

  // Handle map move events
  useMapEvents({
    moveend: () => {
      // Don't trigger callbacks if we're programmatically updating the map
      if (!isUpdatingRef.current) {
        const mapCenter = map.getCenter();
        const mapZoom = map.getZoom();

        if (onMapMove) {
          onMapMove([mapCenter.lat, mapCenter.lng], mapZoom);
        }

        // Debounce bounds change to avoid too many updates
        if (onMapBoundsChange) {
          if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
          }

          boundsChangeTimeoutRef.current = setTimeout(() => {
            const bounds = map.getBounds();
            const isUserInitiated = hasInitializedRef.current; // Only user-initiated after initial setup
            // Debug: MapController moveend
            onMapBoundsChange({
              north: bounds.getNorth(),
              south: bounds.getSouth(),
              east: bounds.getEast(),
              west: bounds.getWest(),
            }, isUserInitiated);
          }, 300); // 300ms debounce
        }
      }
    },
    zoomend: () => {
      // Handle zoom changes for bounds updates
      if (!isUpdatingRef.current && onMapBoundsChange) {
        if (boundsChangeTimeoutRef.current) {
          clearTimeout(boundsChangeTimeoutRef.current);
        }

        boundsChangeTimeoutRef.current = setTimeout(() => {
          const bounds = map.getBounds();
          const isUserInitiated = hasInitializedRef.current; // Only user-initiated after initial setup
          // Debug: MapController zoomend
          onMapBoundsChange({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
          }, isUserInitiated);
        }, 300);
      }
    },
  });

  // Update map view when center/zoom props change
  useEffect(() => {
    if (center && zoom && map) {
      const currentCenter = map.getCenter();
      const currentZoom = map.getZoom();

      // Only update if the center or zoom has actually changed significantly
      const centerChanged = Math.abs(currentCenter.lat - center[0]) > 0.001 ||
        Math.abs(currentCenter.lng - center[1]) > 0.001;
      const zoomChanged = Math.abs(currentZoom - zoom) > 0.1;

      if (centerChanged || zoomChanged) {
        isUpdatingRef.current = true;
        map.setView(center, zoom);
        // Reset the flag after a short delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 100);
      }
    }
  }, [map, center, zoom]);

  // Fit bounds to show all reports when reports change (initial load only)
  useEffect(() => {
    if (reports.length > 0 && !center && !hasInitializedRef.current) {
      // Debug: Fitting map bounds to show all reports
      const bounds = new LatLngBounds(
        reports.map(report => new LatLng(report.location.latitude, report.location.longitude))
      );

      isUpdatingRef.current = true;
      map.fitBounds(bounds, { padding: [20, 20] });

      // Mark as initialized after fitting bounds
      setTimeout(() => {
        hasInitializedRef.current = true;
        isUpdatingRef.current = false;
        // Debug: Map initialization complete
      }, 500);
    }
  }, [map, reports, center]);

  return null;
};

const MapView: React.FC<MapViewProps> = ({
  reports,
  selectedReport,
  onReportSelect,
  center,
  zoom = 12,
  onMapMove,
  isLoading,
  hoveredReport,
  onMapBoundsChange,
}) => {
  const [mapZoom, setMapZoom] = useState(zoom);
  const [clusters, setClusters] = useState<Array<{
    reports: Report[];
    center: [number, number];
    isCluster: boolean;
  }>>([]);

  // Update clusters when reports or zoom changes
  useEffect(() => {
    setClusters(clusterReports(reports, mapZoom));
  }, [reports, mapZoom]);

  // Handle zoom change
  const handleZoomChange = useCallback((newZoom: number) => {
    setMapZoom(newZoom);
  }, []);

  // Default center (Sydney Metro, Australia)
  const defaultCenter: [number, number] = [-33.8688, 151.2093];
  const mapCenter = center || (reports.length > 0 ? undefined : defaultCenter);

  return (
    <div className="relative h-full">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-[999] flex items-center justify-center">
          <div className="flex items-center gap-3 bg-white rounded-lg shadow-lg px-4 py-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cardboard"></div>
            <span className="text-urban font-medium">Loading treasures...</span>
          </div>
        </div>
      )}

      <MapContainer
        center={mapCenter}
        zoom={zoom}
        className="h-full w-full"
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <MapController
          center={center}
          zoom={zoom}
          onMapMove={onMapMove}
          onMapBoundsChange={onMapBoundsChange}
          reports={reports}
        />

        {/* Render clusters and individual markers */}
        {clusters.map((cluster, index) => {
          if (cluster.isCluster && cluster.reports.length > 1) {
            // Render cluster marker
            return (
              <Marker
                key={`cluster-${index}`}
                position={cluster.center}
                icon={createClusterIcon(cluster.reports.length)}
                eventHandlers={{
                  click: () => {
                    // Zoom in to show individual markers
                    if (onMapMove) {
                      onMapMove(cluster.center, Math.min(mapZoom + 2, 18));
                    }
                  },
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h3 className="font-semibold text-urban mb-2">
                      {cluster.reports.length} Treasures
                    </h3>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {cluster.reports.slice(0, 5).map(report => (
                        <button
                          key={report.id}
                          onClick={() => onReportSelect(report)}
                          className="block w-full text-left text-sm text-urban hover:text-cardboard hover:bg-teddy p-1 rounded"
                        >
                          {report.location.suburb} - {report.tags.slice(0, 2).join(', ')}
                        </button>
                      ))}
                      {cluster.reports.length > 5 && (
                        <div className="text-xs text-urban/70 pt-1">
                          +{cluster.reports.length - 5} more...
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          } else {
            // Render individual marker
            const report = cluster.reports[0];
            const isSelected = selectedReport?.id === report.id;
            const isHovered = hoveredReport?.id === report.id;

            return (
              <Marker
                key={report.id}
                position={cluster.center}
                icon={createCustomIcon(report.status, isSelected, isHovered)}
                eventHandlers={{
                  click: () => onReportSelect(report),
                }}
              >
                <Popup>
                  <TreasureMapPopup
                    report={report}
                    onSelect={() => onReportSelect(report)}
                  />
                </Popup>
              </Marker>
            );
          }
        })}
      </MapContainer>

      {/* Map controls */}
      <div className="absolute top-4 right-4 z-[999] flex flex-col gap-2">
        {/* Zoom controls */}
        <div className="bg-white rounded-lg shadow-lg border border-urban/10">
          <button
            onClick={() => {
              const newZoom = Math.min(mapZoom + 1, 18);
              handleZoomChange(newZoom);
              if (onMapMove && mapCenter) {
                onMapMove(mapCenter, newZoom);
              }
            }}
            className="block w-10 h-10 flex items-center justify-center text-urban hover:text-cardboard hover:bg-teddy transition-colors border-b border-urban/10"
          >
            +
          </button>
          <button
            onClick={() => {
              const newZoom = Math.max(mapZoom - 1, 1);
              handleZoomChange(newZoom);
              if (onMapMove && mapCenter) {
                onMapMove(mapCenter, newZoom);
              }
            }}
            className="block w-10 h-10 flex items-center justify-center text-urban hover:text-cardboard hover:bg-teddy transition-colors"
          >
            −
          </button>
        </div>

        {/* Fit bounds button */}
        {reports.length > 0 && (
          <button
            onClick={() => {
              if (onMapMove) {
                // Calculate bounds of all reports
                const lats = reports.map(r => r.location.latitude);
                const lngs = reports.map(r => r.location.longitude);
                const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                onMapMove([centerLat, centerLng], 12);
              }
            }}
            className="bg-white rounded-lg shadow-lg border border-urban/10 p-2 text-urban hover:text-cardboard hover:bg-teddy transition-colors"
            title="Fit all treasures"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[999] bg-white rounded-lg shadow-lg border border-urban/10 p-3">
        <h4 className="font-semibold text-urban text-sm mb-2">Legend</h4>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-verge rounded-full flex items-center justify-center text-xs">🏆</div>
            <span className="text-urban">Active</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-treasure rounded-full flex items-center justify-center text-xs">⏰</div>
            <span className="text-urban">Going Soon</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rust rounded-full flex items-center justify-center text-xs">❌</div>
            <span className="text-urban">Gone</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapView;